/**
 * Manifest save 요청 body 정본 (ICD-MAIN-226 `POST /conversion/update-viewr-spaces`).
 *
 * legacy full save(`ICD-MAIN-156`)와 달리 Main API가 unknown field를 거부하므로,
 * 여기서 만드는 body에 계약에 없는 key를 넣지 않는다.
 */

import type { SpaceManifestEntry } from "./saveSpaceManifest";

export interface ViewRUpdateSpacesRequestBody {
  viewr_gcs_path: string;
  vdb_gcs_path: string;
  vdb_sha256?: string;
  space_manifest: SpaceManifestEntry[];
  usage_snapshot_complete?: boolean;
  used_vse_gcs_paths?: string[];
  used_library_object_ids?: string[];
  /** 같은 저장 시도의 중복 제출이 Job·revision을 중복 생성하지 않게 하는 business idempotency key. */
  idempotency_key?: string;
  save_attempt_id?: string;
  origin_mutation_id?: string;
  user_id?: string;
}

export const VIEWR_UPDATE_SPACES_PATH = "/conversion/update-viewr-spaces";

export const buildViewRUpdateSpacesRequestBody = ({
  normalizedGcsPath,
  vdbGcsPath,
  userId,
  spaceManifest,
  usedVseGcsPaths,
  usedLibraryObjectIds,
  usageSnapshotComplete = false,
  idempotencyKey,
  saveAttemptId,
  originMutationId,
  vdbSha256,
}: {
  normalizedGcsPath: string;
  vdbGcsPath: string;
  userId: string;
  spaceManifest: readonly SpaceManifestEntry[];
  usedVseGcsPaths?: string[];
  usedLibraryObjectIds?: string[];
  usageSnapshotComplete?: boolean;
  idempotencyKey?: string;
  saveAttemptId?: string;
  originMutationId?: string;
  vdbSha256?: string;
}): ViewRUpdateSpacesRequestBody => {
  const body: ViewRUpdateSpacesRequestBody = {
    viewr_gcs_path: normalizedGcsPath,
    vdb_gcs_path: vdbGcsPath,
    user_id: userId,
    space_manifest: spaceManifest.map((entry) => ({
      source: entry.source,
      index: entry.index,
    })),
  };

  // partial VDB에서 전체 usage를 추론할 수 없으므로, 전체 상태를 명시한 저장만 usage를 교체한다.
  if (usageSnapshotComplete) {
    body.usage_snapshot_complete = true;
    body.used_vse_gcs_paths = usedVseGcsPaths ?? [];
    body.used_library_object_ids = usedLibraryObjectIds ?? [];
  }

  const trimmedIdempotencyKey = (idempotencyKey ?? "").trim();
  if (trimmedIdempotencyKey) {
    body.idempotency_key = trimmedIdempotencyKey;
  }

  const trimmedSaveAttemptId = (saveAttemptId ?? "").trim();
  if (trimmedSaveAttemptId) {
    body.save_attempt_id = trimmedSaveAttemptId;
  }

  const trimmedOriginMutationId = (originMutationId ?? "").trim();
  if (trimmedOriginMutationId) {
    body.origin_mutation_id = trimmedOriginMutationId;
  }

  const normalizedVdbSha256 = (vdbSha256 ?? "").trim().toLowerCase();
  if (/^[0-9a-f]{64}$/.test(normalizedVdbSha256)) {
    body.vdb_sha256 = normalizedVdbSha256;
  }

  return body;
};
